import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import type { ContentRegistry } from '@/core/content/registry';
import type { Reward } from '@/core/content/schemas/progression';
import type { TFunction } from '@/core/i18n/translator';
import { miniGameBonusCooldownMs, miniGameCooldownMs } from '@/core/domain/minigames';
import type { Game } from '@/game/Game';
import type { MiniGameIntro } from '@/game/minigames/MiniGameHost';
import type { RecipeId } from '@/game/minigames/cooking/recipes';
import { LockIcon, MiniGameArt, SpeakerIcon } from '@/ui/components/Icons';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import styles from './MiniGameOverlay.module.css';
import { useBack } from '@/ui/back/backStack';
import { Icon } from '@/ui/components/Icon';
import { CookingHud } from './CookingHud';
import { RecipeBook } from './RecipeBook';

/** Progress below this quits at once; from `NEAR_DONE` the sheet also offers "Xong luôn" (minigames.md §1.3). */
const ASK_FROM = 0.3;
const NEAR_DONE = 0.7;

function rewardText(r: Reward, t: TFunction, content: ContentRegistry): string | null {
  if (r.type === 'coins') return t('ui.minigame.reward.coins', { count: r.amount });
  if (r.type === 'stars') return t('ui.minigame.reward.stars', { count: r.amount });
  if (r.type === 'item') {
    const item = content.items.get(r.itemId);
    if (!item) return null;
    return r.qty > 1
      ? t('ui.minigame.reward.item_qty', { name: t(item.nameKey), count: r.qty })
      : t(item.nameKey);
  }
  return null;
}

/**
 * S21 intro, S22 HUD over the shared canvas (✕, title, progress, sound) with the bottom "All done" button,
 * and S23 result card. No timers, no failure, praise only (minigames.md §1.5).
 */
export function MiniGameOverlay({ game }: { game: Game }) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  const run = useStore(game.miniGameUi, (s) => s.run);
  const intro = useStore(game.miniGameUi, (s) => s.intro);
  const muted = useSave((s) => s.settings.muted);
  const [coachHidden, setCoachHidden] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [exitAsk, setExitAsk] = useState<'mid' | 'near' | null>(null);

  useEffect(() => {
    if (!run?.firstStroke) return;
    const timer = setTimeout(() => {
      setCoachHidden(true);
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [run?.firstStroke]);

  // ✕ / Back / Esc (minigames.md §1.3): under 30% leave at once; from 30% ask, pausing the game meanwhile.
  const requestExit = () => {
    const r = game.miniGameUi.getState().run;
    if (!r) return;
    if (r.phase !== 'playing' || r.progress < ASK_FROM) {
      game.miniGames.quit();
      return;
    }
    game.miniGames.setPaused(true);
    // Bếp của bà can always "Xong luôn" (the rest of the dish makes itself, minigames.md §4.6).
    const canFinish = r.canFinish || r.miniGameId === 'mg_cooking';
    setExitAsk(r.progress >= NEAR_DONE && canFinish ? 'near' : 'mid');
  };
  const answerExit = (choice: 'keep' | 'quit' | 'finish') => {
    setExitAsk(null);
    game.miniGames.setPaused(false);
    if (choice === 'quit') game.miniGames.quit();
    else if (choice === 'finish') game.miniGames.finishNow();
  };

  useBack(() => {
    if (exitAsk) answerExit('keep');
    else requestExit();
  }, !!run);

  if (!run) return intro ? <MiniGameIntroCard game={game} intro={intro} /> : null;
  const def = content.miniGames.get(run.miniGameId);
  const level = def?.levels.find((l) => l.level === run.level);
  const tutorial = run.mode === 'tutorial';
  const packing = run.miniGameId === 'mg_packing';
  const cooking = run.miniGameId === 'mg_cooking';
  const recipe = (level?.params as { recipe?: RecipeId } | undefined)?.recipe ?? 'jam';
  const asking = exitAsk !== null && run.phase === 'playing';

  return (
    <div className={styles.overlay} data-testid="minigame">
      <header className={styles.top}>
        <button
          type="button"
          className={styles.close}
          aria-label={t('ui.minigame.exit')}
          onClick={requestExit}
        >
          <Icon name="close" size={26} />
        </button>
        <div className={styles.titleBox}>
          <span className={styles.title}>
            {def ? t(def.nameKey) : ''}: {level ? t(level.nameKey) : ''}
          </span>
          <div
            className={styles.progress}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(run.progress * 100)}
          >
            <div className={styles.fill} style={{ width: `${String(Math.round(run.progress * 100))}%` }} />
          </div>
        </div>
        <span className={styles.percent}>{t('ui.minigame.progress', { value: run.progress })}</span>
        <button
          type="button"
          className={styles.close}
          aria-label={t(muted ? 'ui.minigame.sound_on' : 'ui.minigame.sound_off')}
          aria-pressed={muted}
          data-testid="minigame-sound"
          onClick={() => {
            dispatch({ type: 'updateSettings', patch: { muted: !muted } });
          }}
        >
          <SpeakerIcon muted={muted} />
        </button>
      </header>

      {asking && (
        <div className={styles.bottom}>
          <div className={styles.confirm} role="alertdialog" aria-label={t('ui.minigame.exit')}>
            <p>{t(exitAsk === 'near' ? 'ui.minigame.exit_near' : 'ui.minigame.exit_mid')}</p>
            <div className={styles.row}>
              <button
                type="button"
                className={styles.secondary}
                data-testid="minigame-exit-confirm"
                onClick={() => {
                  answerExit('quit');
                }}
              >
                {t('ui.minigame.exit')}
              </button>
              <button
                type="button"
                className={exitAsk === 'near' ? styles.secondary : styles.primary}
                onClick={() => {
                  answerExit('keep');
                }}
              >
                {t('ui.minigame.keep_playing')}
              </button>
              {exitAsk === 'near' && (
                <button
                  type="button"
                  className={styles.primary}
                  onClick={() => {
                    answerExit('finish');
                  }}
                >
                  {t('ui.minigame.finish_now')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {tutorial && run.phase === 'playing' && !coachHidden && (
        <p className={styles.coach} role="status">
          {run.firstStroke ? t('ui.dusting.coach_good') : t('ui.dusting.coach_start')}
          {!run.firstStroke && <span className={styles.swipe}>{t('ui.dusting.coach_swipe')}</span>}
        </p>
      )}

      {packing && run.phase === 'playing' && !run.firstStroke && (
        <p className={styles.coach} role="status">
          {t('ui.packing.coach')}
        </p>
      )}

      {packing && run.phase === 'playing' && !asking && (
        <div className={styles.bottom}>
          {confirmClose ? (
            <div className={styles.confirm} role="alertdialog" aria-label={t('ui.packing.close_box')}>
              <p>{t('ui.packing.confirm', { pct: 1 - run.progress })}</p>
              <div className={styles.row}>
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => {
                    setConfirmClose(false);
                  }}
                >
                  {t('ui.packing.keep_going')}
                </button>
                <button
                  type="button"
                  className={styles.primary}
                  onClick={() => {
                    setConfirmClose(false);
                    game.miniGames.finishNow();
                  }}
                >
                  {t('ui.packing.close_anyway')}
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.row}>
              <button
                type="button"
                className={styles.secondary}
                data-testid="packing-hint"
                onClick={() => {
                  game.miniGames.hint();
                }}
              >
                {t('ui.packing.hint')}
              </button>
              <button
                type="button"
                className={styles.primary}
                data-testid="packing-close"
                disabled={!run.canFinish}
                onClick={() => {
                  if (run.progress < 0.7) setConfirmClose(true);
                  else game.miniGames.finishNow();
                }}
              >
                {t('ui.packing.close_box')}
              </button>
            </div>
          )}
        </div>
      )}

      {cooking && <CookingHud game={game} recipe={recipe} asking={asking} />}

      {!packing && !cooking && run.phase === 'playing' && run.canFinish && !asking && (
        <div className={styles.bottom}>
          <button
            type="button"
            className={styles.primary}
            onClick={() => {
              game.miniGames.finishNow();
            }}
          >
            {t('ui.minigame.finish')}
          </button>
        </div>
      )}

      {run.phase === 'result' && run.result && (
        <div className={styles.scrim}>
          <section className={styles.card} role="dialog" aria-modal="true" aria-labelledby="mg-result-title">
            <h2 id="mg-result-title" className={styles.cardTitle}>
              {tutorial
                ? t('ui.dusting.tutorial_result')
                : run.result.praiseKey
                  ? t(run.result.praiseKey)
                  : t('ui.minigame.result_title')}
            </h2>
            {run.result.summaryKey && <p className={styles.summary}>{t(run.result.summaryKey)}</p>}
            {packing && run.result.score < 0.7 ? (
              <p className={styles.note}>{t('ui.packing.loose_result')}</p>
            ) : (
              run.result.noteKey && <p className={styles.note}>“{t(run.result.noteKey)}”</p>
            )}
            {run.result.firstClear && <p className={styles.badge}>{t('ui.minigame.first_clear')}</p>}
            {run.result.rewards.length > 0 ? (
              <ul className={styles.rewards}>
                {run.result.rewards.map((r, i) => {
                  const text = rewardText(r, t, content);
                  return text ? <li key={i}>+ {text}</li> : null;
                })}
              </ul>
            ) : (
              <p className={styles.muted}>
                {run.result.cooldownRemainingMs > 0
                  ? t('ui.minigame.cooldown', { time: formatMs(run.result.cooldownRemainingMs) })
                  : t('ui.minigame.no_reward')}
              </p>
            )}
            {(run.result.bonusWaitMs ?? 0) > 0 && (
              <p className={styles.muted}>
                {t('ui.cooking.bonus_wait', { time: formatMs(run.result.bonusWaitMs ?? 0) })}
              </p>
            )}
            <div className={styles.actions}>
              {tutorial ? (
                <button
                  type="button"
                  className={styles.primary}
                  onClick={() => {
                    game.miniGames.close();
                  }}
                >
                  {t('ui.minigame.read_letter')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => {
                      game.miniGames.playAgain();
                    }}
                  >
                    {t('ui.minigame.play_again')}
                  </button>
                  <button
                    type="button"
                    className={styles.primary}
                    onClick={() => {
                      game.miniGames.close();
                    }}
                  >
                    {t('ui.minigame.done')}
                  </button>
                </>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

/** S21 (minigames.md §1.2 step 2): picture, length, level chips (cleared ✓, open, locked + why), reward. */
function MiniGameIntroCard({ game, intro }: { game: Game; intro: MiniGameIntro }) {
  const { t } = useI18n();
  const { content } = useServices();
  const def = content.miniGames.get(intro.miniGameId);
  const cleared = useSave((s) => s.miniGames[intro.miniGameId]?.levelsCleared.join(',') ?? '');
  // Whole seconds, so the card re-renders once a second at most while the cooldown runs.
  const cooldownSec = useSave((s) => (def ? Math.ceil(miniGameCooldownMs(def, s) / 1000) : 0));
  const bonusSec = useSave((s) => (def ? Math.ceil(miniGameBonusCooldownMs(def, s) / 1000) : 0));
  const [book, setBook] = useState(false);

  useBack(() => {
    game.miniGames.closeIntro();
  });

  if (!def) return null;
  const done = new Set(cleared === '' ? [] : cleared.split(',').map(Number));
  const lockedBy = (level: number) => (level > 1 && !done.has(level - 1) ? level - 1 : null);
  const levelDef = def.levels.find((l) => l.level === intro.level);
  const blocker = lockedBy(intro.level);
  const rewardLine =
    blocker !== null
      ? t('ui.minigame.intro.locked', { level: blocker })
      : levelDef && !done.has(levelDef.level)
        ? t('ui.minigame.intro.first_clear', {
            rewards: levelDef.firstClear
              .map((r) => rewardText(r, t, content))
              .filter(Boolean)
              .join(', '),
          })
        : cooldownSec > 0
          ? t('ui.minigame.cooldown', { time: formatMs(cooldownSec * 1000) })
          : t('ui.minigame.intro.repeat', { min: def.repeatReward.min, max: def.repeatReward.max });
  // Bếp của bà: a homemade dish on top of the coins, at most every 5 minutes of play (§4.11).
  const bonusLine =
    levelDef?.repeatBonus && blocker === null && done.has(levelDef.level)
      ? bonusSec > 0
        ? t('ui.cooking.bonus_wait', { time: formatMs(bonusSec * 1000) })
        : t('ui.cooking.bonus')
      : null;

  return (
    <div className={styles.overlay}>
      <div className={styles.scrim}>
        <section
          className={styles.card}
          role="dialog"
          aria-modal="true"
          aria-labelledby="mg-intro-title"
          data-testid="minigame-intro"
        >
          <div className={styles.art}>
            <MiniGameArt game={def.id === 'mg_packing' || def.id === 'mg_cooking' ? def.id : 'mg_dusting'} />
          </div>
          <h2 id="mg-intro-title" className={styles.cardTitle}>
            {t(def.nameKey)}
          </h2>
          <p className={styles.muted}>{t('ui.minigame.intro.duration', { sec: def.durationHintSec })}</p>
          <div className={styles.levels} role="group" aria-label={t('ui.minigame.intro.levels')}>
            {def.levels.map((l) => {
              const state = done.has(l.level) ? 'cleared' : lockedBy(l.level) !== null ? 'locked' : 'open';
              return (
                <button
                  key={l.level}
                  type="button"
                  className={styles.level}
                  aria-pressed={intro.level === l.level}
                  data-state={state}
                  data-testid={`minigame-level-${String(l.level)}`}
                  onClick={() => {
                    game.miniGames.selectLevel(l.level);
                  }}
                >
                  {t('ui.minigame.intro.level', { level: l.level })}
                  {state === 'cleared' && (
                    <>
                      <Icon name="check" size={18} className={styles.check} />
                      <span className="visually-hidden">{t('ui.minigame.intro.cleared')}</span>
                    </>
                  )}
                  {state === 'locked' && <LockIcon label={t('ui.minigame.intro.locked_short')} />}
                </button>
              );
            })}
          </div>
          {levelDef && <p className={styles.levelName}>{t(levelDef.nameKey)}</p>}
          <p className={styles.muted}>{rewardLine}</p>
          {bonusLine && <p className={styles.muted}>{bonusLine}</p>}
          {def.id === 'mg_cooking' &&
            (book ? (
              <RecipeBook
                onClose={() => {
                  setBook(false);
                }}
              />
            ) : (
              <button
                type="button"
                className={styles.bookButton}
                data-testid="recipe-book-open"
                onClick={() => {
                  setBook(true);
                }}
              >
                <Icon name="recipe" size={26} />
                {t('ui.cooking.recipe_book')}
              </button>
            ))}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => {
                game.miniGames.closeIntro();
              }}
            >
              {t('ui.minigame.intro.later')}
            </button>
            <button
              type="button"
              className={styles.primary}
              disabled={blocker !== null}
              data-testid="minigame-play"
              onClick={() => {
                game.miniGames.start(def.id, intro.level, 'normal');
              }}
            >
              {t('ui.minigame.intro.play')}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function formatMs(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, '0')}`;
}
